import { Link } from 'react-router-dom';
import type { CommitteeMember, MemberSummary, TeamCompetition } from '../types/api.types';
import { formatMemberName } from '../utils/members';

interface DueCommitteeRoleTableProps {
  committeeMembers: CommitteeMember[];
  memberSummaries: MemberSummary[];
  teamCompetitions: TeamCompetition[];
}

interface MemberParticipation {
  id: string;
  firstName: string;
  surname: string;
  captainYears: string[];
  committeeMemberYears: string[];
  ryderCupEvents: string[]; // Array of event dates attended
}

// Helper to get the most recent year from an array of years
const getMostRecentYear = (years: string[]): number => {
  if (years.length === 0) return 0;
  return Math.max(...years.map((y) => parseInt(y, 10)));
};

// Build initial participation map from member summaries
const buildParticipationMap = (memberSummaries: MemberSummary[]): Map<string, MemberParticipation> => {
  const participationMap = new Map<string, MemberParticipation>();

  memberSummaries.forEach((member) => {
    participationMap.set(member.id, {
      id: member.id,
      firstName: member.firstName,
      surname: member.surname,
      captainYears: [],
      committeeMemberYears: [],
      ryderCupEvents: [],
    });
  });

  return participationMap;
};

// Add committee participation data to the map
const addCommitteeParticipation = (
  participationMap: Map<string, MemberParticipation>,
  committeeMembers: CommitteeMember[]
): void => {
  committeeMembers.forEach((committee) => {
    if (committee.captain) {
      const participation = participationMap.get(committee.captain);
      if (participation) {
        participation.captainYears.push(committee.season);
      }
    }

    if (committee.committeeMember1) {
      const participation = participationMap.get(committee.committeeMember1);
      if (participation) {
        participation.committeeMemberYears.push(committee.season);
      }
    }

    if (committee.committeeMember2) {
      const participation = participationMap.get(committee.committeeMember2);
      if (participation) {
        participation.committeeMemberYears.push(committee.season);
      }
    }
  });
};

// Add Ryder Cup participation data to the map
const addRyderCupParticipation = (
  participationMap: Map<string, MemberParticipation>,
  lastFiveCompetitions: TeamCompetition[]
): void => {
  lastFiveCompetitions.forEach((comp) => {
    if (comp.competitors) {
      comp.competitors.forEach((competitor) => {
        const participation = participationMap.get(competitor.memberID);
        if (participation && !participation.ryderCupEvents.includes(comp.eventDate)) {
          participation.ryderCupEvents.push(comp.eventDate);
        }
      });
    }
  });

  // Sort each member's events chronologically
  participationMap.forEach((participation) => {
    participation.ryderCupEvents.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  });
};

// Filter and sort participation data based on eligibility criteria
const filterAndSortParticipation = (participationMap: Map<string, MemberParticipation>): MemberParticipation[] => {
  const permanentRoleMembers = new Set(['CL', 'PM', 'GS']);
  const participationArray = Array.from(participationMap.values()).filter(
    (participation) => participation.ryderCupEvents.length > 2 && !permanentRoleMembers.has(participation.id)
  );

  return [...participationArray].sort((a, b) => {
    const aCaptainYears = a.captainYears;
    const bCaptainYears = b.captainYears;
    const aCommitteeYears = a.committeeMemberYears;
    const bCommitteeYears = b.committeeMemberYears;

    // 1. Never held ANY role comes first
    const aHasAnyRole = aCaptainYears.length > 0 || aCommitteeYears.length > 0;
    const bHasAnyRole = bCaptainYears.length > 0 || bCommitteeYears.length > 0;
    if (!aHasAnyRole && bHasAnyRole) return -1;
    if (aHasAnyRole && !bHasAnyRole) return 1;

    // 2. Sort by longest time since holding ANY committee role
    const aMostRecentAny = Math.max(getMostRecentYear(aCaptainYears), getMostRecentYear(aCommitteeYears));
    const bMostRecentAny = Math.max(getMostRecentYear(bCaptainYears), getMostRecentYear(bCommitteeYears));
    if (aMostRecentAny !== bMostRecentAny) {
      return aMostRecentAny - bMostRecentAny; // Earlier years first (longer time since)
    }

    // 3. Finally, sort by surname
    return a.surname.localeCompare(b.surname);
  });
};

// Component for table header
const TableHeader = () => (
  <thead>
    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Member</th>
      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Captain</th>
      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Committee Member</th>
      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>Past 5 Tours</th>
    </tr>
  </thead>
);

// Component for a single participation row
const ParticipationRow = ({ participation, index }: { participation: MemberParticipation; index: number }) => (
  <tr
    style={{
      borderBottom: '1px solid #dee2e6',
      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
    }}
  >
    <td style={{ padding: '12px', fontWeight: 'bold' }}>
      <Link to={`/members/${participation.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
        {formatMemberName(participation)}
      </Link>
    </td>
    <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px' }}>
      {participation.captainYears.length > 0 ? participation.captainYears.join(', ') : '-'}
    </td>
    <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px' }}>
      {participation.committeeMemberYears.length > 0 ? participation.committeeMemberYears.join(', ') : '-'}
    </td>
    <td style={{ padding: '12px', textAlign: 'center' }}>
      {participation.ryderCupEvents.length > 0 ? (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{participation.ryderCupEvents.length} events</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {participation.ryderCupEvents.map((date) => new Date(date).getFullYear()).join(', ')}
          </div>
        </div>
      ) : (
        '-'
      )}
    </td>
  </tr>
);

export default function DueCommitteeRoleTable({
  committeeMembers,
  memberSummaries,
  teamCompetitions,
}: DueCommitteeRoleTableProps) {
  // Build member participation data
  const participationMap = buildParticipationMap(memberSummaries);

  // Add committee and Ryder Cup participation
  addCommitteeParticipation(participationMap, committeeMembers);

  // Get last 5 team competitions (already sorted by date descending)
  const lastFiveCompetitions = teamCompetitions.slice(0, 5);
  addRyderCupParticipation(participationMap, lastFiveCompetitions);

  // Filter and sort based on eligibility criteria
  const sortedParticipation = filterAndSortParticipation(participationMap);

  return (
    <div className="aboutpage">
      <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
        Members who have attended more than 2 of the last 5 Ryd-Her Cup events (excluding permanent roles CL, PM, GS),
        sorted by eligibility for a committee role.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '20px',
            fontSize: '14px',
          }}
        >
          <TableHeader />
          <tbody>
            {sortedParticipation.map((participation, index) => (
              <ParticipationRow key={participation.id} participation={participation} index={index} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
